<template>
  <div class="pt-4 pb-24 px-4">
    <h3 class="text-3xl font-bold mb-5">Signal Logs</h3>

    <!-- Date Filters -->
    <div class="mb-6 space-y-3">
      <div class="flex gap-3 mb-2">
        <input
          type="date"
          v-model="startDate"
          class="block w-full rounded-lg border py-1.5 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset focus:border-transparent"
          :class="[
            $theme.baseTextColor,
            $theme.inputPlaceTextColor,
            $theme.inputRingFocusColor,
            $theme.inputBorderColor,
            $theme.inputRingColor,
            $theme.cardBg,
          ]"
        >
        <input
          type="date"
          v-model="endDate"
          class="block w-full rounded-lg border py-1.5 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset focus:border-transparent"
          :class="[
            $theme.baseTextColor,
            $theme.inputPlaceTextColor,
            $theme.inputRingFocusColor,
            $theme.inputBorderColor,
            $theme.inputRingColor,
            $theme.cardBg,
          ]"
        >
      </div>
      <button
          @click="applyFilter"
          class="w-full py-3.5 rounded-xl font-semibold text-sm transition-transform active:scale-95 shadow-sm"
          :class="[
            $theme.bgBtn,
            $theme.whiteText,
          ]"
      >
        Apply Filter
      </button>
    </div>

    <div v-if="loading" class="rounded-xl overflow-hidden mt-4" :class="[$theme.cardBg]">
      <LoadingBlock />
    </div>

    <template v-else>
      <div v-if="signal_logs?.data?.length" class="space-y-2">
        <div v-for="log in signal_logs.data" :key="log.id"
             class="group rounded-xl p-4 transition-all duration-200 hover:shadow-lg"
             :class="[$theme.cardBg, $theme.cardBorder]">

          <div class="flex items-start justify-between gap-3">
            <!-- Left Section -->
            <div class="flex items-start gap-3 flex-1 min-w-0">
              <!-- Side Indicator -->
              <div class="relative mt-2">
                <div class="w-2 h-2 rounded-full shadow-sm" :class="sideBg(log.params.type)"></div>
                <div class="absolute inset-0 animate-ping rounded-full opacity-40" :class="sideBg(log.params.type)"></div>
              </div>

              <!-- Main Content -->
              <div class="min-w-0 space-y-1.5">
                <div class="flex items-center gap-2">
                  <span class="text-lg font-bold truncate">{{ log.params.ticker }}</span>
                  <span class="text-xs font-semibold px-2 py-1 rounded-md capitalize"
                        :class="typeBadgeStyles(log.type)">
                    {{ log.type.toLowerCase() }}
                  </span>
                </div>

                <div class="flex items-center gap-3 text-sm">
                  <div class="flex items-center gap-1.5">
                    <span class="opacity-70">Side:</span>
                    <span class="font-medium" :class="sideTextColor(log.params.type)">
                      {{ formatSide(log.params.type) }}
                    </span>
                  </div>
                  <div class="w-px h-4 bg-opacity-20" :class="$theme.borderColor"></div>
                  <div class="flex items-center gap-1.5">
                    <span class="opacity-70">Price:</span>
                    <span class="font-mono font-medium">
                      {{ log.params.price || 'N/A' }}
                    </span>
                  </div>
                </div>

                <div class="text-xs flex items-center gap-1.5 opacity-70">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {{ formatDateCompact(log.created_at) }}
                </div>
              </div>
            </div>

            <!-- Right Section -->
            <div class="shrink-0 pl-2 text-right">
              <template v-if="log.user_task">
                <div v-if="log.user_task.status === 'failed'" class="inline-flex items-center gap-1 text-xs px-3 py-1.5 opacity-70">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="#FF5353">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Failed
                </div>
              </template>
              <template v-else>
                <template v-if="log.type === 'OPEN'">
                  <button
                      v-if="isWithinTwoHours(log.created_at)"
                      @click="onSignalProcessing(log)"
                      class="px-4 py-2 text-xs rounded-lg text-white font-semibold transition-transform active:scale-95 shadow-sm"
                      :class="[
                    $theme.bgGray,
                    $theme.whiteText,
                  ]"
                  >
                    Execute
                  </button>
                  <div v-else class="inline-flex items-center gap-1 text-xs px-3 py-1.5 opacity-70">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="#FF5353">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Expired
                  </div>
                </template>
              </template>
            </div>
          </div>
        </div>

        <!-- Load More Button -->
        <div class="mt-6 text-center">
          <button
              @click="loadMoreLogs"
              :disabled="loadingMore || signal_logs.current_page >= signal_logs.last_page"
              class="px-6 py-3.5 rounded-xl font-semibold text-sm transition-transform active:scale-95 w-full shadow-sm"
              :class="[
                $theme.placeBg,
                $theme.cardLabelTextColor,
                (loadingMore || signal_logs.current_page >= signal_logs.last_page)
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              ]"
          >
            <span v-if="loadingMore">Loading...</span>
            <span v-else-if="signal_logs.current_page < signal_logs.last_page">
              Load More ({{ signal_logs.total - signal_logs.data.length }} remaining)
            </span>
            <span v-else>All logs loaded</span>
          </button>
        </div>
      </div>

      <div v-else class="mt-5 rounded-xl overflow-hidden p-8 text-center space-y-3" :class="[$theme.cardBg]">
        <div class="text-4xl opacity-50">🤷♂️</div>
        <p class="opacity-75 font-medium">No signals found</p>
      </div>
    </template>
  </div>

  <TransitionRoot as="template" :show="isSignalProcessing">
    <Dialog class="relative z-50" @close="isSignalProcessing = false">
      <div class="fixed inset-0 bg-gray-950 bg-opacity-45 transition-opacity" />
      <div class="fixed inset-0 z-50 flex items-end justify-center">
        <TransitionChild
            as="template"
            enter="transform transition ease-in-out duration-300"
            enter-from="translate-y-full opacity-0"
            enter-to="translate-y-0 opacity-100"
            leave="transform transition ease-in-out duration-300"
            leave-from="translate-y-0 opacity-100"
            leave-to="translate-y-full opacity-0"
        >
          <DialogPanel class="w-full rounded-t-2xl px-4 pt-5 pb-4 text-left shadow-xl w-full sm:p-6" :class="[$theme.baseBg, $theme.baseTextColor]">
            <div v-if="!submitted">
              <h3 class="text-lg font-bold mb-4">Process Signal</h3>

              <!-- Signal Details -->
              <div class="mb-5 space-y-2 p-4 rounded-xl" :class="$theme.cardBg">
                <div class="flex justify-between">
                  <span class="opacity-70">Type:</span>
                  <span class="font-medium">{{ signalLog.type }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="opacity-70">Ticker:</span>
                  <span class="font-medium">{{ signalLog.params.ticker }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="opacity-70">Price:</span>
                  <span class="font-mono">{{ signalLog.params.price || 'N/A' }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="opacity-70">Leverage:</span>
                  <span class="font-medium">{{ signalLog.params.leverage }}x</span>
                </div>
              </div>

              <!-- Exchanges List -->
              <div class="mb-6">
                <h4 class="text-sm font-semibold mb-3 opacity-80">Select Exchanges:</h4>
                <div class="space-y-2">
                  <label v-for="exchange in exchanges" :key="exchange.id"
                   class="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors"
                   :class="[
                     $theme.cardBg,
                     selectedExchanges.includes(exchange.id) ? 'ring-2' : ''
                   ]">
                    <input type="checkbox"
                           v-model="selectedExchanges"
                           :value="exchange.id"
                           class="hidden">
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1">
                        <span class="font-medium capitalize">{{ exchange.exchange_slug }}</span>
                        <span v-if="exchange.status_connected" class="text-xs px-2 py-1 rounded-full bg-green5e/20 text-green5e">Connected</span>
                      </div>
                      <div class="text-xs opacity-70">
                        Max positions: {{ exchange.max_positions }},
                        Leverage: {{ exchange.leverage }}x
                      </div>
                    </div>

                    <!-- Чек-иконка -->
                    <div v-if="selectedExchanges.includes(exchange.id)" class="shrink-0 ml-2">
                      <svg class="w-5 h-5 text-green5e" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </label>
                </div>
              </div>

              <button
                  @click="processSignal"
                  :disabled="processing"
                  class="w-full py-3.5 rounded-xl font-semibold text-sm transition-transform active:scale-95 shadow-sm"
                  :class="[
                  $theme.bgBtn,
                  $theme.whiteText,
                  processing ? 'opacity-70 cursor-not-allowed' : ''
                ]"
              >
                <span v-if="processing">Processing...</span>
                <span v-else>Process Signal</span>
              </button>
            </div>

            <!-- Success State -->
            <div v-else class="text-center py-4">
              <div class="text-green5e text-4xl mb-3">✓</div>
              <h3 class="text-lg font-bold mb-2">Tasks Created</h3>
              <p class="opacity-80 text-sm mb-4">
                Signal processing started on {{ selectedExchanges.length }} exchanges.
                Please wait for execution...
              </p>
              <button
                  @click="isSignalProcessing = false"
                  class="w-full py-3.5 rounded-xl font-semibold text-sm transition-transform active:scale-95 shadow-sm"
                  :class="[$theme.bgBtn, $theme.whiteText]"
              >
                Close
              </button>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
<script>
import { format } from 'date-fns'
import LoadingBlock from "@/components/LoadingBlock.vue"
import { addBackButton, hideBackButton } from "@/helpers/telegramHelper";
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'

export default {
  components: {
    LoadingBlock,
    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
  },

  data() {
    return {
      loading: true,
      loadingMore: false,
      signal_logs: null,
      startDate: null,
      endDate: null,
      isSignalProcessing: false,
      submitted: false,
      processing: false,
      selectedExchanges: [],
      exchanges: [],
    }
  },

  methods: {
    async onSignalProcessing(log) {
      this.signalLog = log
      this.isSignalProcessing = true
      this.submitted = false
      this.selectedExchanges = []
      await this.loadUserExchanges(log.signal_id)
    },

    async processSignal() {
      if (!this.selectedExchanges.length) return

      this.processing = true

      try {
        const payload = {
          exchange_ids: this.selectedExchanges,
        }

        await this.$api.post(`/signal-logs/${this.signalLog.id}/processing`, payload)
        this.submitted = true

        setTimeout(() => {
          this.isSignalProcessing = false
          this.submitted = false
          this.loadSignalLogs()
        }, 3000)

      } catch (error) {
        console.error('Error processing signal:', error)
        alert('Error processing signal. Please try again.')
      } finally {
        this.processing = false
      }
    },

    loadUserExchanges(signalId) {
      this.$api.get(`/signals/${signalId}/exchanges`).then(({ data }) => {
        this.exchanges = data.data

        console.log(this.exchanges)
      }).catch(e => {
        console.error(e)
      })
    },

    typeBadgeStyles(type) {
      return {
        'bg-green5e/20 text-green5e': type === 'BUY',
        'bg-red/20 text-red': type === 'SELL',
        'bg-orange/20 text-orange': !['BUY', 'SELL'].includes(type)
      }
    },
    sideBg(side) {
      return {
        'bg-green5e': side === true,
        'bg-red': side === false,
        'bg-graya9': typeof side !== 'boolean'
      }
    },
    sideBorderStyles(side) {
      return {
        'border-l-4': true,
        'border-green5e/50': side === true,
        'border-red/50': side === false,
        'border-graya9/50': typeof side !== 'boolean'
      }
    },

    formatDateCompact(date) {
      return format(new Date(date), 'dd MMM HH:mm')
    },

    formatSide(side) {
      return typeof side === 'boolean'
          ? (side ? 'LONG' : 'SHORT')
          : 'N/A'
    },

    sideStyles(side) {
      return {
        'border-l-4': true,
        'border-green5e bg-green5e': side === true,
        'border-red bg-red': side === false,
        'border-graya9 bg-graya9': typeof side !== 'boolean'
      }
    },

    sideTextColor(side) {
      return {
        'text-green5e': side === true,
        'text-red': side === false,
        'text-graya9': typeof side !== 'boolean'
      }
    },

    async loadSignalLogs(page = 1) {
      try {
        this.loading = true
        let url = `/signals/logs?page=${page}&per_page=20`

        if (this.startDate) url += `&start_date=${this.startDate}`
        if (this.endDate) url += `&end_date=${this.endDate}`

        const { data } = await this.$api.get(url)
        this.signal_logs = data
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },

    applyFilter() {
      this.loadSignalLogs(1)
    },

    async loadMoreLogs() {
      if (!this.signal_logs || this.loadingMore) return

      try {
        this.loadingMore = true
        const { data } = await this.$api.get(this.signal_logs.next_page_url)
        this.signal_logs = {
          ...data,
          data: [...this.signal_logs.data, ...data.data]
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.loadingMore = false
      }
    },

    isWithinTwoHours(createdAt) {
      const now = new Date();
      const signalTime = new Date(createdAt);
      const diffInHours = (now - signalTime) / (1000 * 60 * 60); // Convert ms to hours
      return diffInHours <= 2;
    },

    executeSignal(log) {
      // Placeholder for actual execution logic
      alert(`Executing signal: ${log.type} for ${log.params.ticker} at price ${log.params.price}`);
    }
  },

  mounted() {
    this.loadSignalLogs()
    addBackButton(this.$router)
  },

  beforeUnmount() {
    hideBackButton()
  }
}
</script>
